/* Multi-step AI Company Assessment UI. Faithful port of the Claude artifact stepper. */

import {
  AI_GROUP,
  CALL_URL,
  DIMS,
  INDUSTRY_OPTIONS,
  QUESTIONS,
  REVENUE_OPTIONS,
  SEAT,
  STEPS,
  TEAM_MIDPOINT,
  TEAM_SIZE_OPTIONS,
  TOOLS,
  TOTAL_STEPS,
  clearAssessment,
  esc,
  loadAssessment,
  money,
  qIndex,
  saveAssessment,
  scoreAssessment,
  verdictFor,
  type AssessmentState,
  type DimKey,
  type TeamSize,
} from './aas-assessment'
import { ICONS } from './aas-tool-icons'
import { initExitIntent } from './aas-exit-intent'
import { validPhone } from './phone'
import { submitQuestionnaire } from './questionnaire-submission'

const KEYS = ['A', 'B', 'C', 'D']
const LABELS: Record<string, string> = {
  company: 'Your company',
  stack: 'Your stack',
  contact: 'Your report details',
}
const HINTS: Record<string, string> = {
  company: 'Select one per row',
  stack: 'Select everything in use',
  contact: 'Your report appears here',
}

function iconHTML(slug: string, mono: string | undefined, name: string): string {
  if (slug && ICONS[slug]) return `<img src="${ICONS[slug]}" alt="">`
  return `<b>${esc(mono || name.charAt(0))}</b>`
}

function chips(field: string, vals: readonly string[], A: AssessmentState): string {
  return `<div class="aas-opts" data-field="${field}">${vals.map((v) =>
    `<button type="button" class="aas-opt" aria-pressed="${A[field as 'team' | 'industry' | 'revenue'] === v}" value="${esc(v)}">${esc(v)}</button>`
  ).join('')}</div>`
}

function qBlock(i: number, A: AssessmentState): string {
  const q = QUESTIONS[i]!
  return `<div class="aas-q"><span class="aas-ql">${esc(q.q)}</span><p class="aas-qh">${esc(q.h)}</p><div class="aas-qopts" data-q="${i}">${
    q.o.map((o, k) => `<button type="button" class="aas-qopt" data-i="${k}" aria-pressed="${A.ans[i] === k}"><span class="aas-key">${KEYS[k]}</span><span>${esc(o[0])}</span><span class="aas-chk">✓</span></button>`).join('')
  }</div></div>`
}

function stepHTML(step: number, A: AssessmentState, isReport: boolean): string {
  if (isReport) return reportHTML(A)
  const name = STEPS[step]!
  if (name === 'company') {
    return `<div class="aas-estep"><div class="aas-estep-head"><h1 class="aas-h2">First, the shape of the company.</h1><p>So the report is sized to you.</p></div>
      <div class="aas-q"><span class="aas-ql">Team size</span>${chips('team', TEAM_SIZE_OPTIONS, A)}</div>
      <div class="aas-q"><span class="aas-ql">Industry</span>${chips('industry', INDUSTRY_OPTIONS, A)}</div>
      <div class="aas-q"><span class="aas-ql">Annual revenue</span>${chips('revenue', REVENUE_OPTIONS, A)}</div></div>`
  }
  if (name === 'stack') {
    return `<div class="aas-estep"><div class="aas-estep-head"><h1 class="aas-h2">Your stack.</h1><p>Tap everything in use. We bring the map to the call.</p></div>
      <div class="aas-q"><span class="aas-ql">How much of your team uses AI in a typical week?</span>
        <div class="aas-range-out"><span data-aas-usage-out>${A.usage}</span>%<small>of the team</small></div>
        <input type="range" data-aas-usage min="0" max="100" step="5" value="${A.usage}" aria-label="Share of team using AI weekly" style="--v:${A.usage}%">
      </div>
      <div class="aas-q"><span class="aas-ql">Your current stack <small data-aas-tool-count>${A.tools.length} selected</small></span></div>
      ${TOOLS.map((g) => `<div class="aas-tool-group"><h4>${esc(g.g)}</h4><div class="aas-tools">${
        g.items.map(([n, slug, mono]) =>
          `<button type="button" class="aas-tool" aria-pressed="${A.tools.includes(n)}" data-tool="${esc(n)}"><span class="aas-ti">${iconHTML(slug, mono, n)}</span><span>${esc(n)}</span></button>`
        ).join('')
      }</div></div>`).join('')}
      <div class="aas-field"><label for="aas-other-tools">Anything else your team uses</label>
        <input type="text" id="aas-other-tools" data-aas-k="otherTools" placeholder="Other tools, agents, or internal builds" autocomplete="off" value="${esc(A.otherTools)}"></div>
    </div>`
  }
  if (name === 'contact') {
    return `<div class="aas-estep"><div class="aas-estep-head"><h1 class="aas-h2">Your report details.</h1><p>Your report appears here. No email will be sent.</p></div>
      <div class="aas-fields">
        <div class="aas-field"><label for="aas-a-name">Name</label><input type="text" id="aas-a-name" data-aas-k="name" autocomplete="name" placeholder="Your name" value="${esc(A.name)}"></div>
        <div class="aas-field"><label for="aas-a-email">Work email</label><input type="email" id="aas-a-email" data-aas-k="email" autocomplete="email" placeholder="you@company.com" value="${esc(A.email)}"></div>
        <div class="aas-field"><label for="aas-a-phone">Phone number</label><input type="tel" id="aas-a-phone" data-aas-k="phone" autocomplete="tel" inputmode="tel" placeholder="+1 555 123 4567" value="${esc(A.phone)}"></div>
        <div class="aas-field"><label for="aas-a-role">Your role <span class="aas-optional">(optional)</span></label><input type="text" id="aas-a-role" data-aas-k="role" autocomplete="organization-title" placeholder="CEO, COO, Founder…" value="${esc(A.role)}"></div>
      </div><p class="aas-fine">How we handle your details: <a href="/privacy/">Privacy Policy</a>.</p></div>`
  }
  const qi = qIndex(name)
  if (qi >= 0) {
    return `<div class="aas-estep aas-estep-one"><div class="aas-estep-head"><p class="aas-qn">${esc(DIMS[QUESTIONS[qi]!.d].label)}</p></div>${qBlock(qi, A)}</div>`
  }
  return ''
}

function reportHTML(A: AssessmentState): string {
  const { dims, total } = scoreAssessment(A)
  const v = verdictFor(total)
  const order = (Object.keys(DIMS) as DimKey[]).sort((a, b) => (dims[a] ?? 0) - (dims[b] ?? 0))
  const ai = A.tools.filter((t) => AI_GROUP.includes(t)).length
  const mid = TEAM_MIDPOINT[(A.team || '26–50') as TeamSize] || 50
  const findings = [
    { c: (dims.visibility ?? 0) >= 67 ? 'good' : (dims.visibility ?? 0) >= 34 ? 'warn' : '', t: `${A.usage}% of your team uses AI in a typical week. ${(dims.visibility ?? 0) >= 67 ? 'You can see most of that work.' : (dims.visibility ?? 0) >= 34 ? 'You can see usage, not the work.' : "You can't see any of it."}` },
    { c: ai <= 1 ? 'good' : ai === 2 ? 'warn' : '', t: ai === 0 ? 'No AI assistant in the stack yet. You get to start governed instead of cleaning up.' : ai === 1 ? 'One AI assistant, one vendor. Tier permissions and company-wide exclusions still don\'t exist across the rest of your stack.' : `${ai} AI assistants in use with no shared policy. That's ${ai} invoices, ${ai} sets of permissions, and zero oversight.` },
    { c: '', t: `Weakest first: ${order.slice(0, 3).map((d) => DIMS[d].label).join(', ')}. That's the order we'd install in.` },
    { c: '', t: `At ${A.team || 'your size'} people, per-seat plans run about ${money(mid * SEAT)} a year. The install starts at $5,000 with no seat cap.` },
  ]
  const dimCards = (Object.keys(DIMS) as DimKey[]).map((d) => {
    const p = dims[d] ?? 0
    const cls = p >= 67 ? 'g' : p >= 34 ? 'a' : 'r'
    const txt = p >= 67 ? DIMS[d].high : p >= 34 ? DIMS[d].mid : DIMS[d].low
    return `<div class="aas-dim"><div class="aas-dim-h"><b>${esc(DIMS[d].label)}</b><span>${p}</span></div><div class="aas-dim-bar"><i class="${cls}" style="--w:${p}%"></i></div><p>${esc(txt)} <b>We install:</b> ${esc(DIMS[d].install)}</p></div>`
  }).join('')

  const first = A.name ? `${esc(A.name.split(' ')[0]!)}, your` : 'Your'
  return `<div class="aas-estep aas-report">
    <div class="aas-rep-head">
      <div class="aas-ring" data-aas-score-ring style="--p:0"><div><b data-aas-score-num>0</b><small>AI Readiness</small></div></div>
      <div class="aas-rep-copy">
        <span class="aas-verdict ${v[0]}">${esc(v[1])}</span>
        <h2 class="aas-h2">${first} company scores ${total} out of 100.</h2>
        <p>${esc(v[2])}</p>
        <ul class="aas-findings">${findings.map((x) => `<li class="${x.c}">${esc(x.t)}</li>`).join('')}</ul>
      </div>
    </div>
    <div class="aas-dimgrid">${dimCards}</div>
    <div class="aas-rep-actions">
      <a class="pill pill-ink" data-aas-book href="${CALL_URL}" target="_blank" rel="noopener">Book the call, we bring this report</a>
      <button type="button" class="aas-btn aas-btn-ghost" data-aas-copy>Copy my report</button>
      <button type="button" class="aas-btn aas-btn-ghost" data-aas-again>Start over</button>
      <p class="aas-fine">Prepared for ${esc(A.name || 'you')}. Scores reflect your answers today; the call turns them into an install plan.</p>
      <p class="aas-summary-box" data-aas-summary></p>
      <p class="aas-fine"><a href="/cost-savings-calculator/">Open the Cost Savings Calculator</a> to compare seats vs install for your team size.</p>
    </div>
  </div>`
}

function summaryText(A: AssessmentState): string {
  const { dims, total } = scoreAssessment(A)
  return [
    `AI Readiness: ${total}/100`,
    (Object.keys(DIMS) as DimKey[]).map((d) => `${DIMS[d].label} ${dims[d]}`).join(' · '),
    `${A.team} people · ${A.industry} · ${A.revenue}`,
    `Stack: ${(A.tools.join(', ') || 'none selected')}${A.otherTools.trim() ? ` · ${A.otherTools.trim()}` : ''}`,
    `AI usage: ${A.usage}% weekly`,
    `Contact: ${A.name} · ${A.email}${A.role ? ` · ${A.role}` : ''}`,
    `Answers: ${A.ans.map((a, i) => `${i + 1}:${a >= 0 ? KEYS[a] : '-'}`).join(' ')}`,
  ].join('\n')
}

function validate(step: number, A: AssessmentState): string {
  const name = STEPS[step]!
  if (name === 'company') {
    if (!A.team) return 'Pick your team size.'
    if (!A.industry) return 'Pick your industry.'
    if (!A.revenue) return 'Pick a revenue range so the report is sized to you.'
  }
  if (name === 'stack' && !A.tools.length && !A.otherTools.trim()) {
    return 'Select at least one tool, or tell us what your team uses.'
  }
  const qi = qIndex(name)
  if (qi >= 0 && (A.ans[qi] ?? -1) < 0) return 'Pick whatever is closest to true.'
  if (name === 'contact') {
    if (!A.name.trim()) return 'Add your name.'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(A.email.trim())) return 'Add a valid work email.'
    if (!validPhone(A.phone)) return 'Add a valid phone number.'
  }
  return ''
}

export function initQuestionnaire(root: ParentNode = document) {
  const saved = loadAssessment()
  let A = saved.A
  let step = Math.min(saved.step, TOTAL_STEPS - 1)
  let busy = false
  let showingReport = false
  const sendingEnabled = import.meta.env.VITE_BORG_QUESTIONNAIRE_ENABLED === 'true'
  const reduce = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches

  const stage = root.querySelector('[data-aas-q-stage]') as HTMLElement | null
  const err = root.querySelector('[data-aas-q-err]') as HTMLElement | null
  const nextBtn = root.querySelector('[data-aas-q-next]') as HTMLButtonElement | null
  const backBtn = root.querySelector('[data-aas-q-back]') as HTMLButtonElement | null
  const nav = root.querySelector('[data-aas-q-nav]') as HTMLElement | null
  const stepLabel = root.querySelector('[data-aas-q-step-label]')
  const stepHint = root.querySelector('[data-aas-q-step-hint]')
  const prog = root.querySelector('[data-aas-q-prog]')
  if (!stage) return { get step() { return step }, get A() { return A } }

  if (prog) prog.innerHTML = STEPS.map(() => '<i></i>').join('')

  const finishReport = () => {
    const book = root.querySelector('[data-aas-book]') as HTMLAnchorElement | null
    if (book) book.href = CALL_URL
    const box = root.querySelector('[data-aas-summary]')
    if (box) box.textContent = summaryText(A)
  }

  const render = () => {
    const name = STEPS[step]!
    const isRep = showingReport
    stage.innerHTML = stepHTML(step, A, isRep)
    prog?.querySelectorAll('i').forEach((i, k) => i.classList.toggle('on', k <= Math.min(step, TOTAL_STEPS - 1)))
    if (stepLabel) {
      stepLabel.textContent = isRep
        ? 'Your AI Readiness report'
        : qIndex(name) >= 0
          ? `Question ${qIndex(name) + 1} of ${QUESTIONS.length}`
          : `STEP ${step + 1} OF ${TOTAL_STEPS} · ${LABELS[name]?.toUpperCase() ?? ''}`
    }
    if (stepHint) {
      stepHint.textContent = isRep
        ? `Prepared ${new Date().toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}`
        : (HINTS[name] || 'Pick one. It moves on by itself.')
    }
    if (backBtn) backBtn.hidden = step === 0 || isRep
    if (nav) nav.hidden = isRep
    if (nextBtn) nextBtn.textContent = name === 'contact' ? 'Get my report' : 'Continue'
    if (err) err.textContent = ''
    if (isRep) {
      const { total } = scoreAssessment(A)
      const ring = root.querySelector('[data-aas-score-ring]') as HTMLElement | null
      const num = root.querySelector('[data-aas-score-num]')
      const t0 = performance.now()
      const tick = (t: number) => {
        const k = reduce ? 1 : Math.min(1, (t - t0) / 1400)
        const e = 1 - Math.pow(1 - k, 3)
        if (ring) ring.style.setProperty('--p', (total * e).toFixed(1))
        if (num) num.textContent = String(Math.round(total * e))
        if (k < 1) requestAnimationFrame(tick)
      }
      requestAnimationFrame(tick)
      finishReport()
    }
  }

  const go = (n: number) => {
    if (busy) return
    const card = stage.querySelector('.aas-estep')
    const done = () => {
      busy = false
      step = n
      showingReport = n >= TOTAL_STEPS
      if (showingReport) step = TOTAL_STEPS - 1
      render()
      saveAssessment(A, Math.min(step, TOTAL_STEPS - 1))
      root.querySelector('[data-aas-q-form]')?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
    }
    if (card && !reduce) {
      busy = true
      card.classList.add('out')
      setTimeout(done, 240)
    } else done()
  }

  const next = async () => {
    if (busy || showingReport) return
    const m = validate(step, A)
    if (m) { if (err) err.textContent = m; return }
    if (STEPS[step] === 'contact') {
      saveAssessment(A, step)
      if (sendingEnabled) {
        busy = true
        const controls = [
          ...stage.querySelectorAll<HTMLInputElement | HTMLButtonElement>('input, button'),
          ...[nextBtn, backBtn].filter((control): control is HTMLButtonElement => control !== null),
        ].map((control) => ({ control, disabled: control.disabled }))
        controls.forEach(({ control }) => { control.disabled = true })
        if (nextBtn) nextBtn.textContent = 'Saving…'
        try {
          await submitQuestionnaire(A)
        } catch {
          if (err) err.textContent = 'We couldn’t save your assessment. Your answers are still here. Please try again.'
          return
        } finally {
          busy = false
          controls.forEach(({ control, disabled }) => { control.disabled = disabled })
          if (nextBtn) nextBtn.textContent = 'Get my report'
        }
      }
      go(TOTAL_STEPS)
      return
    }
    go(step + 1)
  }

  const pick = (qi: number, k: number, wrap?: Element | null) => {
    A.ans[qi] = k
    ;(wrap || stage).querySelectorAll('.aas-qopt').forEach((b) => {
      const btn = b as HTMLElement
      btn.setAttribute('aria-pressed', Number(btn.dataset.i) === k ? 'true' : 'false')
    })
    saveAssessment(A, step)
    if (err) err.textContent = ''
    const at = step
    if (qIndex(STEPS[step]!) === qi) {
      setTimeout(() => { if (step === at && !busy) go(step + 1) }, reduce ? 120 : 380)
    }
  }

  nextBtn?.addEventListener('click', next)
  backBtn?.addEventListener('click', () => { if (step > 0 && !showingReport) go(step - 1) })
  root.querySelector('[data-aas-q-form]')?.addEventListener('submit', (e) => {
    e.preventDefault()
    if (!showingReport) next()
  })

  stage.addEventListener('click', (e) => {
    if (busy) return
    const t = e.target as HTMLElement
    const o = t.closest('.aas-opt') as HTMLButtonElement | null
    if (o) {
      const f = (o.parentElement as HTMLElement).dataset.field as 'team' | 'industry' | 'revenue'
      A = { ...A, [f]: o.value }
      o.parentElement?.querySelectorAll('.aas-opt').forEach((b) => b.setAttribute('aria-pressed', b === o ? 'true' : 'false'))
      saveAssessment(A, step)
      return
    }
    const tool = t.closest('.aas-tool') as HTMLElement | null
    if (tool) {
      const n = tool.dataset.tool || ''
      const i = A.tools.indexOf(n)
      if (i < 0) A.tools.push(n)
      else A.tools.splice(i, 1)
      tool.setAttribute('aria-pressed', String(i < 0))
      const count = root.querySelector('[data-aas-tool-count]')
      if (count) count.textContent = `${A.tools.length} selected`
      saveAssessment(A, step)
      return
    }
    const q = t.closest('.aas-qopt') as HTMLElement | null
    if (q) {
      pick(Number((q.parentElement as HTMLElement).dataset.q), Number(q.dataset.i), q.parentElement)
      return
    }
    if (t.closest('[data-aas-again]')) {
      A = { ...A, ans: Array(QUESTIONS.length).fill(-1), tools: [], otherTools: '' }
      clearAssessment()
      showingReport = false
      go(0)
      return
    }
    if (t.closest('[data-aas-copy]')) {
      const btn = root.querySelector('[data-aas-copy]') as HTMLButtonElement | null
      const box = root.querySelector('[data-aas-summary]')
      const txt = summaryText(A)
      const done = () => { if (btn) { btn.textContent = 'Copied'; setTimeout(() => { btn.textContent = 'Copy my report' }, 1800) } }
      const fallback = () => {
        if (!box) return
        const r = document.createRange()
        r.selectNodeContents(box)
        const s = getSelection()
        s?.removeAllRanges(); s?.addRange(r)
        if (btn) { btn.textContent = 'Selected. Press copy.'; setTimeout(() => { btn.textContent = 'Copy my report' }, 2200) }
      }
      try { navigator.clipboard.writeText(txt).then(done, fallback) } catch { fallback() }
    }
  })

  stage.addEventListener('input', (e) => {
    if (busy) return
    const el = e.target as HTMLInputElement
    if (el.matches('[data-aas-usage]')) {
      A.usage = Number(el.value)
      const out = root.querySelector('[data-aas-usage-out]')
      if (out) out.textContent = String(A.usage)
      el.style.setProperty('--v', `${A.usage}%`)
      saveAssessment(A, step)
      return
    }
    if (el.dataset.aasK) {
      const k = el.dataset.aasK as keyof AssessmentState
      ;(A as Record<string, unknown>)[k] = el.value
      saveAssessment(A, step)
    }
  })

  document.addEventListener('keydown', (e) => {
    if (busy || showingReport || e.defaultPrevented || e.isComposing || e.repeat) return
    const target = e.target instanceof HTMLElement ? e.target : null
    if (!target) return
    const qi = qIndex(STEPS[step]!)
    const letter = KEYS.indexOf((e.key || '').toUpperCase())
    if (e.metaKey || e.ctrlKey || e.altKey) return
    if (qi >= 0 && letter >= 0 && !target.closest('a, input, textarea, select, [contenteditable="true"]') && (target.closest('[data-aas-q-form]') || target === document.body)) {
      e.preventDefault()
      if (letter < QUESTIONS[qi]!.o.length) pick(qi, letter, stage.querySelector('.aas-qopts'))
      return
    }
    // Buttons and links keep their native keyboard activation. In particular,
    // reading the privacy policy must never advance or submit the assessment.
    if (e.key !== 'Enter' || !stage.contains(target) || !target.matches('input[data-aas-k]')) return
    e.preventDefault()
    next()
  })

  // Exit intent disabled on assessment (artifact: current === "assessment")
  initExitIntent(root, { disabled: true })

  render()
  return { get step() { return step }, get A() { return A }, get showingReport() { return showingReport } }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initQuestionnaire(document), { once: true })
  } else {
    initQuestionnaire(document)
  }
}
